import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTier } from '../hooks/useTier';
import { db, storage } from '../config/firebase';
import { doc, setDoc, getDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { createContactCode, getUserContactCode, getTimeRemaining, formatTimeRemaining } from '../services/contactCodeService';

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'S. Korea', flag: '🇰🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
];

export default function UserProfile() {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const { tier: currentTier, isPremium, isTeam } = useTier();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [recordingsCount, setRecordingsCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [profilePublic, setProfilePublic] = useState(true);
  const [contactCode, setContactCode] = useState(null);
  const [contactCodeLoading, setContactCodeLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showName, setShowName] = useState(true);
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showProfilePic, setShowProfilePic] = useState(true);

  const [profileData, setProfileData] = useState({
    photoURL: '',
    firstName: '',
    lastName: '',
    displayName: '',
    bio: '',
    phone: '',
    countryCode: '+1',
    socialLinks: {
      github: '',
      linkedin: '',
      instagram: '',
      twitter: '',
      website: ''
    }
  });

  const bioCharLimit = 500;
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  useEffect(() => {
    if (user) {
      loadProfile();
      loadDataCounts();
    }
  }, [user]);

  // Update timer for contact code expiration
  useEffect(() => {
    if (!contactCode || profilePublic) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [contactCode, profilePublic]);

  const handleGenerateContactCode = async () => {
    if (profilePublic) {
      toast.error('Contact codes are only for private profiles');
      return;
    }

    setContactCodeLoading(true);
    try {
      const result = await createContactCode(user.uid, profileData.displayName);
      setContactCode(result.code);
      const remaining = getTimeRemaining(result.expiresAt);
      setTimeRemaining(remaining);
      toast.success('Contact code generated successfully');
    } catch (error) {
      console.error('Error generating contact code:', error);
      toast.error('Failed to generate contact code');
    } finally {
      setContactCodeLoading(false);
    }
  };

  const handleCopyContactCode = () => {
    if (!contactCode) return;
    navigator.clipboard.writeText(contactCode);
    toast.success('Contact code copied to clipboard');
  };

  const handleTogglePrivacy = (newValue) => {
    setProfilePublic(newValue);
    if (!newValue && !contactCode) {
      // If switching to private and no code, show message
      toast.info('Generate a contact code to share with others');
    }
  };

  const loadDataCounts = async () => {
    setLoadingCounts(true);
    try {
      // Count recordings
      const recordingsRef = collection(db, 'users', user.uid, 'recordings');
      const recordingsSnap = await getDocs(recordingsRef);
      setRecordingsCount(recordingsSnap.size);

      // Count notes across all meetings
      const historyRef = collection(db, 'users', user.uid, 'meetingHistory');
      const historySnap = await getDocs(historyRef);

      let totalNotes = 0;
      for (const historyDoc of historySnap.docs) {
        const notesRef = collection(db, 'meetings', historyDoc.id, 'notes');
        const notesSnap = await getDocs(notesRef);
        // Count only user's own notes
        const userNotes = notesSnap.docs.filter(noteDoc => {
          const note = noteDoc.data();
          return note.userId === user.uid && !note.archived;
        });
        totalNotes += userNotes.length;
      }
      setNotesCount(totalNotes);
    } catch (error) {
      console.error('Error loading data counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const profileRef = doc(db, 'users', user.uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        setProfileData({
          photoURL: data.photoURL || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          displayName: data.displayName || '',
          bio: data.bio || '',
          phone: data.phone || '',
          countryCode: data.countryCode || '+1',
          socialLinks: {
            github: data.socialLinks?.github || '',
            linkedin: data.socialLinks?.linkedin || '',
            instagram: data.socialLinks?.instagram || '',
            twitter: data.socialLinks?.twitter || '',
            website: data.socialLinks?.website || ''
          }
        });

        // Load privacy settings (defaults to true/showing if not set)
        setProfilePublic(data.profilePublic !== false);
        setShowName(data.showName !== false);
        setShowEmail(data.showEmail !== false);
        setShowPhone(data.showPhone !== false);
        setShowProfilePic(data.showProfilePic !== false);

        // Load contact code if private profile
        if (data.profilePublic === false) {
          const code = await getUserContactCode(user.uid);
          if (code) {
            setContactCode(code.code);
            const remaining = getTimeRemaining(code.expiresAt);
            setTimeRemaining(remaining);
          }
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxFileSize) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Create storage reference
      const storageRef = ref(storage, `profile-pictures/${user.uid}/${Date.now()}_${file.name}`);

      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload error:', error);
          toast.error('Failed to upload image');
          setUploading(false);
          setUploadProgress(0);
        },
        async () => {
          // Upload complete
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setProfileData(prev => ({ ...prev, photoURL: downloadURL }));
          toast.success('Image uploaded successfully');
          setUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (saving) return;

    // Validate first name
    if (profileData.firstName.trim().length < 2) {
      toast.error('First name must be at least 2 characters');
      return;
    }

    // Validate last name
    if (profileData.lastName.trim().length < 2) {
      toast.error('Last name must be at least 2 characters');
      return;
    }

    // Validate display name
    if (profileData.displayName.trim().length < 2) {
      toast.error('Display name must be at least 2 characters');
      return;
    }

    // Validate bio length
    if (profileData.bio.length > bioCharLimit) {
      toast.error(`Bio must be less than ${bioCharLimit} characters`);
      return;
    }

    setSaving(true);
    try {
      const profileRef = doc(db, 'users', user.uid);
      await setDoc(profileRef, {
        photoURL: profileData.photoURL,
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        displayName: profileData.displayName.trim(),
        bio: profileData.bio.trim(),
        phone: profileData.phone.trim(),
        countryCode: profileData.countryCode,
        profilePublic: profilePublic,
        showName: showName,
        showEmail: showEmail,
        showPhone: showPhone,
        showProfilePic: showProfilePic,
        socialLinks: {
          github: profileData.socialLinks.github.trim(),
          linkedin: profileData.socialLinks.linkedin.trim(),
          instagram: profileData.socialLinks.instagram.trim(),
          twitter: profileData.socialLinks.twitter.trim(),
          website: profileData.socialLinks.website.trim()
        },
        updatedAt: serverTimestamp()
      }, { merge: true });

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#000000] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#06c] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#1d1d1f] dark:text-white font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-h-[85vh] overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-1">
            Profile
          </h1>
          <p className="text-sm text-[#6e6e73] dark:text-[#86868b]">
            Manage your public profile information
          </p>
        </div>

        {/* Profile Photo Section */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Profile Picture
          </h2>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#06c] to-[#0055d4] flex items-center justify-center">
                {profileData.photoURL ? (
                  <img
                    src={profileData.photoURL}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl text-white font-semibold">
                    {profileData.firstName ? profileData.firstName.charAt(0).toUpperCase() : user.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-white text-sm font-semibold">{uploadProgress}%</div>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </button>
              <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-2">
                Max 5MB. JPG, PNG, or GIF
              </p>
              {uploading && (
                <div className="mt-3">
                  <div className="w-full bg-[#d2d2d7] dark:bg-[#2c2c2e] rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#06c] to-[#0055d4] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Basic Information
          </h2>

          <div className="space-y-4">
            {/* First Name and Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                  First Name *
                </label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="John"
                  className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Doe"
                  className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                Display Name *
              </label>
              <input
                type="text"
                value={profileData.displayName}
                onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="How you'll appear to others (e.g., 'John D' or 'JD')"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
              <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                This is how you'll appear in meetings, chat, and to other users
              </p>
            </div>

            {/* Email (Read-only) */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={user.email || ''}
                readOnly
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#86868b] cursor-not-allowed text-sm"
              />
              <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                Email cannot be changed
              </p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                Bio
              </label>
              <textarea
                value={profileData.bio}
                onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about yourself..."
                rows={3}
                maxLength={bioCharLimit}
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all resize-none text-sm"
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73]">
                  Brief description for your profile
                </p>
                <p className={`text-xs ${profileData.bio.length > bioCharLimit - 50 ? 'text-red-500' : 'text-[#86868b] dark:text-[#6e6e73]'}`}>
                  {profileData.bio.length}/{bioCharLimit}
                </p>
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                Phone Number
              </label>
              <div className="flex gap-2">
                <select
                  value={profileData.countryCode}
                  onChange={(e) => setProfileData(prev => ({ ...prev, countryCode: e.target.value }))}
                  className="px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
                >
                  {COUNTRY_CODES.map(({ code, country, flag }) => (
                    <option key={code} value={code}>
                      {flag} {code} {country}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="123 456 7890"
                  className="flex-1 px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Social Links
          </h2>

          <div className="space-y-3">
            {/* GitHub */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  GitHub
                </div>
              </label>
              <input
                type="url"
                value={profileData.socialLinks.github}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, github: e.target.value }
                }))}
                placeholder="https://github.com/username"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
            </div>

            {/* LinkedIn */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  LinkedIn
                </div>
              </label>
              <input
                type="url"
                value={profileData.socialLinks.linkedin}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, linkedin: e.target.value }
                }))}
                placeholder="https://linkedin.com/in/username"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
            </div>

            {/* Instagram */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </div>
              </label>
              <input
                type="url"
                value={profileData.socialLinks.instagram}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, instagram: e.target.value }
                }))}
                placeholder="https://instagram.com/username"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
            </div>

            {/* Twitter/X */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Twitter/X
                </div>
              </label>
              <input
                type="url"
                value={profileData.socialLinks.twitter}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, twitter: e.target.value }
                }))}
                placeholder="https://twitter.com/username"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-xs font-medium text-[#1d1d1f] dark:text-white mb-1.5">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Website
                </div>
              </label>
              <input
                type="url"
                value={profileData.socialLinks.website}
                onChange={(e) => setProfileData(prev => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, website: e.target.value }
                }))}
                placeholder="https://yourwebsite.com"
                className="w-full px-3 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:bg-white dark:focus:bg-[#3c3c3e] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all text-sm"
              />
            </div>
          </div>
        </div>

        {/* Privacy & Contact Code */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Privacy & Contacts
          </h2>

          <div className="space-y-4">
            {/* Profile Visibility Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg">
              <div>
                <h3 className="text-sm font-semibold text-[#1d1d1f] dark:text-white">
                  Public Profile
                </h3>
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                  {profilePublic
                    ? 'Anyone can find and add you by searching'
                    : 'Only people with your contact code can add you'}
                </p>
              </div>
              <button
                onClick={() => handleTogglePrivacy(!profilePublic)}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                  profilePublic
                    ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                    : 'bg-[#d2d2d7] dark:bg-[#1c1c1e]'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    profilePublic ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Info Visibility Controls */}
            {profilePublic && (
              <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs font-semibold text-[#1d1d1f] dark:text-white mb-2">
                  What information can others see?
                </p>

                {/* Show Name Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[#1d1d1f] dark:text-white">
                    First & Last Name
                  </label>
                  <button
                    onClick={() => setShowName(!showName)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showName
                        ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                        : 'bg-[#d2d2d7] dark:bg-[#424245]'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        showName ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Show Email Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[#1d1d1f] dark:text-white">
                    Email Address
                  </label>
                  <button
                    onClick={() => setShowEmail(!showEmail)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showEmail
                        ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                        : 'bg-[#d2d2d7] dark:bg-[#424245]'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        showEmail ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Show Phone Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[#1d1d1f] dark:text-white">
                    Phone Number
                  </label>
                  <button
                    onClick={() => setShowPhone(!showPhone)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showPhone
                        ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                        : 'bg-[#d2d2d7] dark:bg-[#424245]'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        showPhone ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Show Profile Pic Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[#1d1d1f] dark:text-white">
                    Profile Picture
                  </label>
                  <button
                    onClick={() => setShowProfilePic(!showProfilePic)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showProfilePic
                        ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                        : 'bg-[#d2d2d7] dark:bg-[#424245]'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        showProfilePic ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Contact Code Section */}
            {!profilePublic && (
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <h3 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">
                  Contact Code
                </h3>
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mb-4">
                  Share this code with people you want to connect with. Code expires after 15 minutes.
                </p>

                {contactCode ? (
                  <div className="space-y-3">
                    {/* Code Display */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="flex items-center justify-between p-3 bg-white dark:bg-[#1c1c1e] rounded-lg border border-[#d2d2d7] dark:border-[#2c2c2e]">
                          <code className="text-lg font-bold text-[#06c] font-mono tracking-widest">
                            {contactCode}
                          </code>
                          <button
                            onClick={handleCopyContactCode}
                            className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] rounded-lg transition-colors"
                            title="Copy code"
                          >
                            <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expiration Timer */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#86868b] dark:text-[#6e6e73]">
                        Expires in: <span className="font-semibold text-orange-500">{formatTimeRemaining(timeRemaining)}</span>
                      </span>
                      {timeRemaining === 0 && (
                        <button
                          onClick={handleGenerateContactCode}
                          disabled={contactCodeLoading}
                          className="text-[#06c] hover:text-[#0055d4] font-semibold disabled:opacity-50"
                        >
                          {contactCodeLoading ? 'Generating...' : 'Generate New'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleGenerateContactCode}
                    disabled={contactCodeLoading}
                    className="w-full py-2 px-4 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white font-medium rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {contactCodeLoading ? 'Generating...' : 'Generate Contact Code'}
                  </button>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Subscription Management */}
        <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-5 mb-4 shadow-sm border border-[#d2d2d7] dark:border-[#2c2c2e]">
          <h2 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4">
            Subscription
          </h2>

          {/* Current Tier */}
          <div className="mb-4 p-4 bg-gradient-to-r from-[#06c]/10 to-[#0055d4]/10 rounded-lg border border-[#06c]/20">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-md font-semibold text-[#1d1d1f] dark:text-white">
                  Current Plan: <span className="text-[#06c]">{currentTier}</span>
                </h3>
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-1">
                  {currentTier === 'free' && 'Basic features for personal use'}
                  {currentTier === 'pro' && 'Advanced features and unlimited recordings'}
                  {currentTier === 'team' && 'Full team collaboration features'}
                </p>
              </div>
              <div className="px-3 py-1.5 bg-[#06c] text-white rounded-full text-xs font-semibold">
                {currentTier.toUpperCase()}
              </div>
            </div>

            {/* Data Usage Warning */}
            {!loadingCounts && (recordingsCount > 0 || notesCount > 0) && (
              <div className="mt-3 p-3 bg-white dark:bg-[#2c2c2e] rounded-lg border border-[#d2d2d7] dark:border-[#3c3c3e]">
                <div className="flex items-start gap-2 text-xs text-[#86868b] dark:text-[#8e8e93]">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-[#1d1d1f] dark:text-white mb-1">Your Data:</p>
                    <p>You have <strong>{recordingsCount}</strong> recording{recordingsCount !== 1 ? 's' : ''} and <strong>{notesCount}</strong> note{notesCount !== 1 ? 's' : ''} saved</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subscription Options */}
          <div className="space-y-3">
            {currentTier === 'free' && (
              <>
                {/* Upgrade to Pro */}
                <button
                  onClick={() => window.location.href = '/dashboard?upgrade=true'}
                  className="w-full p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Upgrade to Pro</h4>
                      <p className="text-xs text-white/80">Unlock unlimited recordings, advanced notes, and more</p>
                    </div>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20 text-xs text-left">
                    <p className="text-white/90 font-semibold mb-1">What you'll gain:</p>
                    <ul className="space-y-0.5 text-white/80">
                      <li>• Unlimited recordings (vs 1 recording)</li>
                      <li>• Advanced meeting notes with sharing</li>
                      <li>• Priority support</li>
                      <li>• Custom meeting templates</li>
                    </ul>
                  </div>
                </button>

                {/* Upgrade to Team */}
                <button
                  onClick={() => window.location.href = '/dashboard?upgrade=true'}
                  className="w-full p-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Upgrade to Team</h4>
                      <p className="text-xs text-white/80">Everything in Pro + team collaboration features</p>
                    </div>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20 text-xs text-left">
                    <p className="text-white/90 font-semibold mb-1">What you'll gain:</p>
                    <ul className="space-y-0.5 text-white/80">
                      <li>• All Pro features</li>
                      <li>• Team workspaces and shared recordings</li>
                      <li>• Advanced analytics</li>
                      <li>• Admin controls</li>
                    </ul>
                  </div>
                </button>
              </>
            )}

            {currentTier === 'pro' && (
              <>
                {/* Upgrade to Team */}
                <button
                  onClick={() => window.location.href = '/dashboard?upgrade=true'}
                  className="w-full p-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Upgrade to Team</h4>
                      <p className="text-xs text-white/80">Add team collaboration features</p>
                    </div>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/20 text-xs text-left">
                    <p className="text-white/90 font-semibold mb-1">What you'll gain:</p>
                    <ul className="space-y-0.5 text-white/80">
                      <li>• Team workspaces</li>
                      <li>• Shared recordings and notes</li>
                      <li>• Advanced analytics</li>
                      <li>• Admin controls</li>
                    </ul>
                  </div>
                </button>

                {/* Downgrade to Free */}
                <button
                  onClick={() => {
                    if (recordingsCount > 1 || notesCount > 0) {
                      if (!window.confirm(`⚠️ Warning: Downgrading will limit you to 1 recording. You currently have ${recordingsCount} recordings and ${notesCount} notes.\n\nYou will lose:\n• ${recordingsCount - 1} recordings (only your most recent will be kept)\n• All ${notesCount} notes\n• Advanced features\n\nThis action cannot be undone. Continue?`)) {
                        return;
                      }
                    }
                    window.location.href = '/dashboard'; // TODO: Implement downgrade
                  }}
                  className="w-full p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-lg hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition-all border border-[#d2d2d7] dark:border-[#3c3c3e]"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Downgrade to Free</h4>
                      <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">Lose access to Pro features</p>
                    </div>
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  {(recordingsCount > 1 || notesCount > 0) && (
                    <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-900/30 text-xs text-left">
                      <p className="text-red-600 dark:text-red-400 font-semibold mb-1">⚠️ You will lose:</p>
                      <ul className="space-y-0.5 text-red-600 dark:text-red-400">
                        {recordingsCount > 1 && <li>• {recordingsCount - 1} recording{recordingsCount - 1 !== 1 ? 's' : ''} (only 1 allowed on free)</li>}
                        {notesCount > 0 && <li>• All {notesCount} note{notesCount !== 1 ? 's' : ''}</li>}
                        <li>• Advanced meeting features</li>
                      </ul>
                    </div>
                  )}
                </button>
              </>
            )}

            {currentTier === 'team' && (
              <>
                {/* Downgrade to Pro */}
                <button
                  onClick={() => {
                    if (window.confirm(`Downgrade to Pro?\n\nYou will lose:\n• Team workspaces\n• Team collaboration features\n• Admin controls\n\nYour recordings and notes will be preserved. Continue?`)) {
                      window.location.href = '/dashboard'; // TODO: Implement downgrade
                    }
                  }}
                  className="w-full p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-lg hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition-all border border-[#d2d2d7] dark:border-[#3c3c3e]"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Downgrade to Pro</h4>
                      <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">Keep Pro features, lose team collaboration</p>
                    </div>
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <div className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-900/30 text-xs text-left">
                    <p className="text-orange-600 dark:text-orange-400 font-semibold mb-1">You will lose:</p>
                    <ul className="space-y-0.5 text-orange-600 dark:text-orange-400">
                      <li>• Team workspaces</li>
                      <li>• Shared team recordings</li>
                      <li>• Admin controls</li>
                    </ul>
                  </div>
                </button>

                {/* Downgrade to Free */}
                <button
                  onClick={() => {
                    if (recordingsCount > 1 || notesCount > 0) {
                      if (!window.confirm(`⚠️ Warning: Downgrading to Free will limit you to 1 recording. You currently have ${recordingsCount} recordings and ${notesCount} notes.\n\nYou will lose:\n• ${recordingsCount - 1} recordings (only your most recent will be kept)\n• All ${notesCount} notes\n• All Team and Pro features\n\nThis action cannot be undone. Continue?`)) {
                        return;
                      }
                    }
                    window.location.href = '/dashboard'; // TODO: Implement downgrade
                  }}
                  className="w-full p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-lg hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition-all border border-[#d2d2d7] dark:border-[#3c3c3e]"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-semibold mb-1">Downgrade to Free</h4>
                      <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">Basic features only</p>
                    </div>
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  {(recordingsCount > 1 || notesCount > 0) && (
                    <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-900/30 text-xs text-left">
                      <p className="text-red-600 dark:text-red-400 font-semibold mb-1">⚠️ You will lose:</p>
                      <ul className="space-y-0.5 text-red-600 dark:text-red-400">
                        {recordingsCount > 1 && <li>• {recordingsCount - 1} recording{recordingsCount - 1 !== 1 ? 's' : ''} (only 1 allowed on free)</li>}
                        {notesCount > 0 && <li>• All {notesCount} note{notesCount !== 1 ? 's' : ''}</li>}
                        <li>• All Team and Pro features</li>
                      </ul>
                    </div>
                  )}
                </button>
              </>
            )}

            {/* Cancel Subscription */}
            {(isPremium || isTeam) && (
              <button
                onClick={() => {
                  const dataWarning = (recordingsCount > 1 || notesCount > 0)
                    ? `\n\nYou will lose:\n• ${recordingsCount > 1 ? `${recordingsCount - 1} recordings` : ''}\n• ${notesCount > 0 ? `All ${notesCount} notes` : ''}`
                    : '';

                  if (window.confirm(`Cancel your ${currentTier} subscription?${dataWarning}\n\nYour subscription will remain active until the end of your billing period, then you'll be downgraded to Free.\n\nContinue?`)) {
                    window.location.href = '/dashboard'; // TODO: Implement cancellation
                  }
                }}
                className="w-full p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-all border border-red-200 dark:border-red-900/30 text-sm font-medium"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end sticky bottom-0 bg-white dark:bg-[#1c1c1e] py-3 -mx-6 px-6 border-t border-[#d2d2d7] dark:border-[#2c2c2e]">
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white px-6 py-2.5 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Profile'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
